import chroma from 'chroma-js';

/**
 * Shared lightning-strike age styling, modelled on maps.blitzortung.org:
 * colour encodes a strike's age within a rolling window — newest white,
 * fading through yellow/orange/red to dark red at the end of the window.
 *
 * This is the single source of truth consumed by both map overlays
 * (webapp maplibre layer + mobile markers) and both legends, so the map
 * and its legend can never drift apart.
 */

/** Rolling age window for lightning strikes (Blitzortung shows ~20 min). */
export const LIGHTNING_AGE_WINDOW_MIN = 20;
export const LIGHTNING_AGE_WINDOW_MS = LIGHTNING_AGE_WINDOW_MIN * 60 * 1000;

/** Age (minutes) → colour stops. Newest first, oldest last. */
export const LIGHTNING_AGE_STOPS: ReadonlyArray<readonly [number, string]> = [
  [0, '#FFFFFF'], // white — just struck
  [4, '#FFE34D'], // pale yellow
  [8, '#FFA500'], // orange
  [13, '#FF2A00'], // red
  [20, '#8B0000'], // dark red — about to be pruned
];

/** Thin dark edge so near-white fresh strikes stay legible against the halo. */
export const LIGHTNING_STROKE_COLOR = '#37474F';

/** White halo so the bolt reads on dark basemaps and busy radar rasters. */
export const LIGHTNING_HALO_COLOR = '#FFFFFF';

/**
 * Lightning-bolt glyph geometry, shared by the webapp (rasterised symbol
 * sprite) and mobile (react-native-svg) so both maps draw the identical mark.
 * A "zap" polygon centred in a 24-unit box, padded to 28 so the halo never
 * clips. Each strike is drawn as: white halo → dark edge → age-coloured fill.
 */
export const LIGHTNING_BOLT_POINTS = '13 2 3 14 12 14 11 22 21 10 12 10';
export const LIGHTNING_BOLT_VIEWBOX = '-2 -2 28 28';
/** On-screen size (px) of the glyph box for a fresh strike (before age scale). */
export const LIGHTNING_BOLT_FRESH_PX = 24;
/** Stroke width (glyph user units) of the white halo and the dark edge. */
export const LIGHTNING_HALO_WIDTH = 5;
export const LIGHTNING_EDGE_WIDTH = 2;

const clampAge = (ageMin: number) =>
  Math.max(0, Math.min(LIGHTNING_AGE_WINDOW_MIN, ageMin));

const ageScale = chroma
  .scale(LIGHTNING_AGE_STOPS.map(([, color]) => color))
  .domain(LIGHTNING_AGE_STOPS.map(([min]) => min));

/** Colour for a strike of the given age in minutes (clamped to the window). */
export const lightningAgeColor = (ageMin: number): string =>
  ageScale(clampAge(ageMin)).hex();

/** Opacity for a strike of the given age (fresh fully opaque, oldest faded). */
export const lightningAgeOpacity = (ageMin: number): number => {
  const t = clampAge(ageMin) / LIGHTNING_AGE_WINDOW_MIN;
  return Number((1 - 0.6 * t).toFixed(2)); // 1.0 → 0.4
};

/** Marker radius (px) for a strike of the given age (fresh largest). */
export const lightningAgeRadius = (ageMin: number): number => {
  const t = clampAge(ageMin) / LIGHTNING_AGE_WINDOW_MIN;
  return Number((6 - 3 * t).toFixed(2)); // 6 → 3
};

/** Glyph scale (multiplier on the fresh size) for a strike of the given age. */
export const lightningAgeScale = (ageMin: number): number => {
  const t = clampAge(ageMin) / LIGHTNING_AGE_WINDOW_MIN;
  return Number((1 - 0.45 * t).toFixed(3)); // 1.0 → 0.55 (fresh largest)
};

/**
 * maplibre `interpolate` expression mapping the per-feature `ageMin` property
 * to the same glyph scale as {@link lightningAgeScale} (used for `icon-size`).
 */
export const lightningScaleInterpolate = (ageProp = 'ageMin') => [
  'interpolate',
  ['linear'],
  ['get', ageProp],
  0,
  lightningAgeScale(0),
  LIGHTNING_AGE_WINDOW_MIN,
  lightningAgeScale(LIGHTNING_AGE_WINDOW_MIN),
];

/**
 * maplibre `interpolate` expression mapping the per-feature `ageMin`
 * property to the same continuous colour ramp as {@link lightningAgeColor}.
 */
export const lightningColorInterpolate = (ageProp = 'ageMin') => [
  'interpolate',
  ['linear'],
  ['get', ageProp],
  ...LIGHTNING_AGE_STOPS.flatMap(([min, color]) => [min, color]),
];

/** Evenly spaced legend swatches (minutes + colour) across the age window. */
export const lightningLegendSwatches = (
  count = 6,
): Array<{ageMin: number; color: string}> =>
  Array.from({length: count}, (_, i) => {
    const ageMin = (LIGHTNING_AGE_WINDOW_MIN * i) / (count - 1);
    return {ageMin, color: lightningAgeColor(ageMin)};
  });

/** CSS `linear-gradient(...)` stops string for the webapp legend bar. */
export const lightningGradientCss = (): string => {
  const stops = LIGHTNING_AGE_STOPS.map(
    ([min, color]) =>
      `${color} ${Math.round((min / LIGHTNING_AGE_WINDOW_MIN) * 100)}%`,
  );
  return `linear-gradient(to right, ${stops.join(', ')})`;
};
